import { useEffect, useRef, useState } from "react";
import { Input, Spinner } from "@material-tailwind/react";
import { t } from "i18next";
import { useWorkorderParts } from "../../../hooks/parts/useWorkorderParts";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { createPortal } from "react-dom";

interface PartSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSelectPart: (part: EasyTech.Part) => void;
  workorderId: string;
  targetType?: "workorder" | "machine" | "hose";
  machineId?: string;
  hoseId?: string;
  existingPartIds?: string[];
  spanTwoColumns?: boolean;
}

/**
 * Composant pour la recherche de pièces détachées.
 * Affiche un champ de recherche et une liste de résultats sous forme de dropdown,
 * qui s'affiche toujours au-dessus de tout grâce à un React Portal.
 *
 * @param {string} value - Valeur du champ de recherche
 * @param {(value: string) => void} onChange - Callback lors de la saisie
 * @param {(part: EasyTech.Part) => void} onSelectPart - Callback lors de la sélection d'une pièce
 * @param {string} workorderId - ID du Workorder
 * @param {"workorder"|"machine"|"hose"} [targetType] - Type de cible pour la recherche
 * @param {string} [machineId] - ID de la machine (optionnel)
 * @param {string} [hoseId] - ID du flexible (optionnel)
 *
 * @returns {JSX.Element}
 */
export default function PartSearchInput({
  value,
  onChange,
  onSelectPart,
  workorderId,
  targetType = "workorder",
  machineId = "",
  hoseId,
  existingPartIds = [],
  spanTwoColumns = false,
}: PartSearchInputProps) {
  /**
   * Hook custom pour gérer la recherche de pièces.
   * Fournit la query, les résultats, et les setters associés.
   */
  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    searchParts,
    setSearchResults,
    isSearchLoading
  } = useWorkorderParts({
    workorderId,
    targetType,
    machineId,
    hoseId,
  });

  /**
   * Ref sur l'input pour calculer dynamiquement la position du dropdown.
   */
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Ref sur le conteneur du dropdown pour détecter les clics à l'extérieur.
   */
  const dropdownRef = useRef<HTMLDivElement>(null);

  /**
   * Position de l'input (top, left, width, bottom) pour positionner le dropdown.
   */
  const [inputPosition, setInputPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    bottom: 0
  });

  /**
   * recherche sans résultats
   */
  const [hasSearchedWithNoResults, setHasSearchedWithNoResults] = useState(false);
  
  /**
   * recherche a été effectuée
   */
  const [hasPerformedSearch, setHasPerformedSearch] = useState(false);

  /**
   * Pièce sélectionnée
   */
  const [hasSelectedPart, setHasSelectedPart] = useState(false);

  /**
   * État pour savoir si l'utilisateur est en train de saisir (pour éviter la recherche automatique à l'initialisation)
   */
  const [isTyping, setIsTyping] = useState(false);

  const showResults = searchResults.length > 0;

  useEffect(() => {
    if (value !== searchQuery) {
      setSearchQuery(value);
      setIsTyping(false);
    }
  }, [value, setSearchQuery, searchQuery]);

  useEffect(() => {
    if (!isTyping) return;

    const delayDebounce = setTimeout(() => {
      if (searchQuery.trim().length >= 3) {
        setHasPerformedSearch(true);
        searchParts(searchQuery);
      } else {
        setHasSearchedWithNoResults(false);
        setHasPerformedSearch(false);
        setHasSelectedPart(false);
      }
    }, 500);
    
    return () => clearTimeout(delayDebounce);
  }, [searchQuery, searchParts, isTyping]);

  useEffect(() => {
    if (isSearchLoading) {
      setHasSearchedWithNoResults(false);
      return;
    }

    if (hasPerformedSearch && searchQuery.trim().length >= 3 && searchResults.length === 0 && !hasSelectedPart) {
      setHasSearchedWithNoResults(true);
      
      const timer = setTimeout(() => {
        setHasSearchedWithNoResults(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    } else if (searchResults.length > 0 || hasSelectedPart) {
      setHasSearchedWithNoResults(false);
    }
  }, [isSearchLoading, searchResults, searchQuery, hasPerformedSearch, hasSelectedPart]);

  // Fermer le dropdown lorsqu'on clique à l'extérieur
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        (!dropdownRef.current || !dropdownRef.current.contains(event.target as Node))
      ) {
        if (searchResults.length > 0) {
          setSearchResults([]);
          setIsTyping(false);
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchResults, setSearchResults, setIsTyping]);

  const updatePosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      let dropdownWidth = rect.width;

      if (spanTwoColumns) {
        const td = inputRef.current.closest("td");
        if (td && td.nextElementSibling) {
          const nextTd = td.nextElementSibling as HTMLElement;
          const nextTdRect = nextTd.getBoundingClientRect();
          dropdownWidth = nextTdRect.right - rect.left - 12;
        }
      }

      setInputPosition({
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, dropdownWidth),
        height: rect.height,
        bottom: rect.bottom + window.scrollY
      });
    }
  };

  useEffect(() => {
    if (showResults) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [showResults]);

  return (
    <div className="relative">
      <Input
        value={searchQuery}
        onChange={(e) => {
          setIsTyping(true);
          setSearchQuery(e.target.value);
          onChange(e.target.value);
          if (hasSearchedWithNoResults) {
            setHasSearchedWithNoResults(false);
          }
          setHasPerformedSearch(false);
          setHasSelectedPart(false);
        }}
        placeholder={t("workorder:parts:searchPlaceholder")}
        icon={
          isSearchLoading ? (
            <Spinner className="w-5 h-5" />
          ) : (
            <MagnifyingGlassIcon className="w-5 h-5" />
          )
        }
        inputRef={inputRef}
        error={hasSearchedWithNoResults}
        className={hasSearchedWithNoResults ? "!border-red-500" : ""}
      />

      {showResults && createPortal(
        <div
          ref={dropdownRef}
          className="z-[9999] bg-white shadow-lg max-h-60 overflow-y-auto rounded-md border border-gray-200"
          style={{
            position: "absolute",
            top: inputPosition.bottom + 4,
            left: inputPosition.left,
            width: inputPosition.width,
          }}
        >
          {searchResults.map(part => {
            const isIntegrated = existingPartIds.includes(part.id);

            return (
            <div
              key={part.id}
              className={`p-2 transition-colors border-b border-gray-100 last:border-b-0 ${
                isIntegrated 
                  ? 'cursor-not-allowed bg-white' 
                  : 'hover:bg-[#1B7AF5] hover:text-white cursor-pointer group'
              }`}
              onClick={() => {
                if (isIntegrated) return;
                onSelectPart(part);
                setIsTyping(false);
                setSearchResults([]);
                setHasSearchedWithNoResults(false);
                setHasSelectedPart(true);
              }}
            >
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <span className={`font-medium ${isIntegrated ? 'text-gray-400' : 'group-hover:text-white text-gray-900'}`}>
                    {part.label}
                  </span>
                  <span className={`text-sm ${isIntegrated ? 'text-gray-400' : 'group-hover:text-white/80 text-gray-500'}`}>
                    {part.ref}
                  </span>
                </div>
                {isIntegrated && (
                  <span className="text-red-600 text-sm mt-0.5">
                     {t("workorder:parts:error:alreadyIntegrated")}
                  </span>
                )}
              </div>
            </div>
          )})}
        </div>,
        document.body
      )}
    </div>
  );
} 